import { MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { getPdb } from "../../api/ApiService";
import { mapMarket } from "../../config/mapMarket";
import { routesConfig } from "../../config/routes";
import { useHasSelectedProducts } from "../../hooks/useHasSelectedProducts";
import { Market } from "../../interfaces/api/Market";
import { cookies } from "../../shared/helpers/cookies";
import { action } from "../../store/action";
import { useIsLoadedFromSummary } from "../../store/hooks/useIsLoadedFromSummary";
import { useMarket } from "../../store/hooks/useMarket";
import ConfirmDialog from "../Dialog/ConfirmDialog";
import { extraMarkets } from "./extraMarkets";
import Flag from "./Flag";
import "./MarketComponent.scss";

const MarketComponent = () => {
  const { t } = useTranslation(["marketstep", "popup"]);
  const dispatch = useDispatch();
  const isLoadedFromSummary = useIsLoadedFromSummary();
  const market = useMarket();
  const hasSelectedProducts = useHasSelectedProducts();
  const [selectFlagMarket, setSelectFlagMarket] = useState<string>("");
  const [oldMarket, setOldMarket] = useState<string>("");
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [markets, setMarkets] = useState([]);

  useEffect(() => {
    if (!markets || markets.length === 0) {
      getPdb("api/products/markets/productguide").then((result) => {
        const pdbMarkets = result as Market[];
        if (pdbMarkets.length === 0 && !extraMarkets.length) {
          return;
        }
        const sortedMarkets = pdbMarkets.concat(extraMarkets);

        sortedMarkets.sort((a, b) => a.name.localeCompare(b.name));
        setMarkets(sortedMarkets);
      });
    }
  }, [markets]);

  useEffect(() => {
    if (market !== undefined) {
      const flagMarket = cookies.getFlagMarket();

      if (flagMarket && !isLoadedFromSummary) {
        setSelectFlagMarket(flagMarket);
        return;
      }
      setSelectFlagMarket(market);
    }
  }, [market, isLoadedFromSummary]);

  const onChange = (event: SelectChangeEvent<string>) => {
    const cultureValue = event.target.value as string;

    setOldMarket(market);
    dispatch(action.saveMarket(cultureValue));

    if (hasSelectedProducts) {
      setShowConfirmDialog(true);
      return;
    }

    marketChanged(cultureValue);
  };

  const marketChanged = (cultureValue: string) => {
    const enableNext = cultureValue ? true : false;
    dispatch(action.saveWizardEnableNext(enableNext));

    if (cultureValue) {
      const lowerCaseMarket = cultureValue.toLowerCase();
      const correctMarket = mapMarket[lowerCaseMarket]
        ? mapMarket[lowerCaseMarket]
        : cultureValue;
      cookies.setMarket(cultureValue);
      cookies.setFlagMarket(cultureValue);
      dispatch(action.saveMarket(correctMarket));
      const url = routesConfig[correctMarket].marketStep;
        window.location.href = url;
    }
  };

  if (!markets || markets.length === 0) {
    return <></>;
  }

  const onDialogOk = () => {
    marketChanged(market);
    setShowConfirmDialog(false);
  };

  const onDialogClose = () => {
    dispatch(action.saveMarket(oldMarket));
    setShowConfirmDialog(false);
  };

  return (
    <>
      <div className="marketContainer">
        <div className="marketLabel">{t("markettexts.searchmarket")}</div>
        <Select
          id="select-market"
          value={selectFlagMarket}
          displayEmpty
          inputProps={{ "aria-label": "Without label" }}
          onChange={onChange}
          fullWidth
          variant="filled"
        >
          <MenuItem value="" key={"market-choose"}>
            <div className="marketItem">
              <span>{t("markets.market")}</span>
            </div>
          </MenuItem>
          {markets &&
            markets.map((market, index) => {
              return (
                <MenuItem value={market.culture} key={`market-${index}`}>
                  <div className="marketItem">
                    <Flag culture={market.culture}></Flag>
                    <span>{t("markets." + market.culture)}</span>
                  </div>
                </MenuItem>
              );
            })}
        </Select>
      </div>
      <ConfirmDialog
        dialogHeader={t("important", { ns: "popup" })}
        text={t("areyousure", { ns: "popup" })}
        okButtonText={t("clearchoises", { ns: "popup" })}
        cancelButtonText={t("cancel", { ns: "popup" })}
        open={showConfirmDialog}
        onClose={onDialogClose}
        onCancelClick={onDialogClose}
        onOkClick={onDialogOk}
      ></ConfirmDialog>
    </>
  );
};

export default MarketComponent;
